import { faCancel, faCheck, faChevronDown, faChevronRight, faCompressAlt, faEdit, faExclamationTriangle, faExpandAlt, faPlus, faTrash } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useState } from "react";
import { Badge, Button, Card } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import TextFieldAtom from "../../../../components/atoms/text-field/text-field";
import {
    costTrackingApi,
    useCreateManualEntryMutation,
    useDeleteManualEntryMutation,
    useUpdateCostTrackingMutation,
    useUpdateManualEntryMutation,
} from "../../../../store/services/costTrackingService";
import { formatCurrencyToLocale, formatValueToLocale, showToast } from "../../../../utils/method";
import AddManualEntryModal from "./AddManualEntryModal";
import "./CostGroupHierarchy.scss";

function CostGroupHierarchy({
    hierarchy,
    summary,
    costTrackingId,
    costGroups = [],
    showGross = true,
    showOnlyWithData = true,
    canEdit = false,
}) {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { projectId } = useParams();
    const [updateCostTracking] = useUpdateCostTrackingMutation();
    const [createManualEntry] = useCreateManualEntryMutation();
    const [updateManualEntry] = useUpdateManualEntryMutation();
    const [deleteManualEntry] = useDeleteManualEntryMutation();
    const [expandedGroups, setExpandedGroups] = useState(new Set());
    const [riskValues, setRiskValues] = useState({});
    const [showManualEntryModal, setShowManualEntryModal] = useState(false);
    const [editingManualEntry, setEditingManualEntry] = useState(null);
    const [manualEntryValues, setManualEntryValues] = useState({});

    // Check if a category has any non-zero financial data
    const categoryHasData = (category) => {
        const t = category.totals || {};
        return (t.lvAmount || 0) !== 0 || (t.supplementAmount || 0) !== 0 ||
            (t.supplementOpenAmount || 0) !== 0 || (t.invoiceAmount || 0) !== 0 ||
            (t.measuredAmountNet || 0) !== 0 || (t.contractSumNet || 0) !== 0;
    };

    // Initialize risk values from hierarchy data
    React.useEffect(() => {
        if (hierarchy?.grouped) {
            const initialRiskValues = {};
            hierarchy.grouped.forEach(category => {
                if (category.items) {
                    category.items.forEach(item => {
                        // Format to 2 decimal places
                        const riskValue = item.risk !== null && item.risk !== undefined && item.risk !== ''
                            ? parseFloat(item.risk).toFixed(2)
                            : '';
                        initialRiskValues[item.id] = riskValue;
                    });
                }
            });
            setRiskValues(initialRiskValues);
        }
    }, [hierarchy]);

    const handleRiskChange = (itemId, value) => {
        setRiskValues(prev => ({
            ...prev,
            [itemId]: value
        }));
    };

    const handleRiskBlur = async (itemId) => {
        const riskValue = parseFloat(riskValues[itemId]) || 0;

        // Format to 2 decimal places for display
        setRiskValues(prev => ({
            ...prev,
            [itemId]: riskValue.toFixed(2)
        }));

        if (costTrackingId && hierarchy && projectId) {
            const patch = dispatch(
                costTrackingApi.util.updateQueryData(
                    "getCostTracking",
                    projectId,
                    (draft) => {
                        draft?.costTrackingData?.hierarchy?.grouped?.forEach((category) => {
                            category.items?.forEach((item) => {
                                if (item.id === itemId) {
                                    item.risk = riskValue;
                                }
                            });
                        });
                    },
                ),
            );

            try {
                const updatedHierarchy = {
                    ...hierarchy,
                    grouped: hierarchy.grouped.map(category => ({
                        ...category,
                        items: category.items.map(item =>
                            item.id === itemId
                                ? { ...item, risk: riskValue }
                                : item
                        )
                    }))
                };

                const costTrackingData = {
                    summary: summary || {},
                    hierarchy: updatedHierarchy
                };

                await updateCostTracking({
                    projectId,
                    costTrackingId,
                    costTrackingData,
                }).unwrap();
            } catch (error) {
                patch.undo();
                console.error('Failed to save risk value:', error);
            }
        }
    };

    const toggleGroup = (groupId) => {
        const newExpanded = new Set(expandedGroups);
        if (newExpanded.has(groupId)) {
            newExpanded.delete(groupId);
        } else {
            newExpanded.add(groupId);
        }
        setExpandedGroups(newExpanded);
    };

    const expandAll = () => {
        const allGroupIds = new Set();
        if (hierarchy?.grouped) {
            hierarchy.grouped.forEach(category => {
                allGroupIds.add(`category-${category.mainCategory}`);
            });
        }
        setExpandedGroups(allGroupIds);
    };

    const collapseAll = () => {
        setExpandedGroups(new Set());
    };

    const handleAddManualEntry = () => {
        setShowManualEntryModal(true);
    };

    const handleSaveManualEntry = async (entryData) => {
        if (!costTrackingId || !projectId) {
            showToast(t("costTracking.no-active-version"), { type: "error" });
            return;
        }

        try {
            await createManualEntry({
                projectId,
                costTrackingId,
                ...entryData,
            }).unwrap();

            setShowManualEntryModal(false);

            showToast(t("costTracking.manual-entry-saved"), { type: "success" });
        } catch (error) {
            showToast(
                error?.data?.message || error?.message || t("costTracking.manual-entry-error"),
                { type: "error" }
            );
        }
    };

    const handleCloseManualEntryModal = () => {
        setShowManualEntryModal(false);
    };

    const handleEditManualEntry = (entryId) => {
        setEditingManualEntry(entryId);
    };

    const handleCancelEdit = () => {
        setEditingManualEntry(null);
        setManualEntryValues({});
    };

    const handleManualEntryFieldChange = (entryId, field, value) => {
        setManualEntryValues(prev => ({
            ...prev,
            [entryId]: {
                ...(prev[entryId] || {}),
                [field]: value
            }
        }));
    };

    const handleSaveManualEntryEdit = async (entryId, currentData) => {
        try {
            const updates = manualEntryValues[entryId] || {};
            const updatedData = {
                costGroupId: updates.costGroupId || currentData.costGroupId,
                companyName: updates.companyName !== undefined ? updates.companyName : currentData.companyName,
                awardAmount: updates.awardAmount !== undefined ? updates.awardAmount : currentData.contractSumNet - currentData.supplementAmount,
                supplementAmount: updates.supplementAmount !== undefined ? updates.supplementAmount : currentData.supplementAmount,
                riskAmount: updates.riskAmount !== undefined ? updates.riskAmount : currentData.risk,
                actualInvoicedAmount: updates.actualInvoicedAmount !== undefined ? updates.actualInvoicedAmount : currentData.invoiceAmount,
                measuredNetAmount: updates.measuredNetAmount !== undefined ? updates.measuredNetAmount : currentData.measuredAmountNet,
                notes: updates.notes !== undefined ? updates.notes : currentData.notes
            };

            await updateManualEntry({
                projectId,
                entryId,
                ...updatedData,
            }).unwrap();

            setEditingManualEntry(null);
            setManualEntryValues({});
            showToast(t("costTracking.manual-entry-updated"), { type: "success" });
        } catch (error) {
            showToast(error?.data?.message || error?.message || t("costTracking.manual-entry-update-error"), { type: "error" });
        }
    };

    const handleDeleteManualEntry = async (entryId) => {
        if (!window.confirm(t("costTracking.confirm-delete-manual-entry"))) {
            return;
        }

        try {
            await deleteManualEntry({ projectId, entryId }).unwrap();
            showToast(t("costTracking.manual-entry-deleted"), { type: "success" });
        } catch (error) {
            showToast(error?.data?.message || error?.message || t("costTracking.manual-entry-delete-error"), { type: "error" });
        }
    };

    const renderCategoryGroup = (category) => {
        const categoryId = `category-${category.mainCategory}`;
        const isExpanded = expandedGroups.has(categoryId);
        const isUnassigned = category.mainCategory === "unassigned";

        // Get entry type badge info
        const getEntryTypeBadge = () => {
            if (!category.entryType) return null;

            const badgeConfig = {
                internal: { variant: 'primary', label: 'I', title: t('costTracking.entry-type.internal') },
                external: { variant: 'success', label: 'E', title: t('costTracking.entry-type.external') },
                mixed: { variant: 'warning', label: 'M', title: t('costTracking.entry-type.mixed') }
            };

            const config = badgeConfig[category.entryType];
            if (!config) return null;

            return (
                <Badge
                    bg={config.variant}
                    className="ms-2 cost-tracking-entry-type-badge"
                    title={config.title}
                >
                    {config.label}
                </Badge>
            );
        };

        return (
            <>
                {/* Category Header Row */}
                <div
                    key={categoryId}
                    className={`cost-tracking-row cost-category-header cost-tracking-row--pointer ${isUnassigned ? 'table-warning' : 'table-light'}`}
                    onClick={() => toggleGroup(categoryId)}
                >
                    {/* Fixed Columns */}
                    <div className="cost-tracking-row-fixed">
                        <div className="cost-tracking-cell cost-tracking-cell--w-180 py-3">
                            <div className="d-flex align-items-center">
                                <FontAwesomeIcon
                                    icon={isExpanded ? faChevronDown : faChevronRight}
                                    className="me-3 text-primary"
                                />
                                {isUnassigned ? (
                                    <div className="d-flex align-items-center">
                                        <FontAwesomeIcon icon={faExclamationTriangle} className="me-2 text-warning" />
                                        <strong className="text-warning">{category.label}</strong>
                                    </div>
                                ) : (
                                    <div className="d-flex align-items-center">
                                        <strong>∑ {category.label}</strong>
                                        {getEntryTypeBadge()}
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="cost-tracking-cell cost-tracking-cell--w-150 py-3" />
                        <div className="cost-tracking-cell cost-tracking-cell--w-200 py-3">
                            <span className="fw-bold text-muted">{category.name}</span>
                        </div>
                        <div className="cost-tracking-cell cost-tracking-cell--w-130 text-end py-3">
                            <span className="fw-bold">{formatCurrencyToLocale(category.totals.lvAmount)}</span>
                        </div>
                        <div className="cost-tracking-cell cost-tracking-cell--w-130 text-end py-3">
                            <span className="fw-bold text-info">{formatCurrencyToLocale(category.totals.supplementAmount)}</span>
                        </div>
                    </div>

                    {/* Scrollable Columns */}
                    <div className={`cost-tracking-row-scrollable${!showGross ? ' cost-tracking-row-scrollable--compact' : ''}`}>
                        <div className="cost-tracking-cell cost-tracking-cell--w-125 text-end py-3">
                            <span className="fw-bold text-secondary">{formatCurrencyToLocale(category.totals.contractSumNet || 0)}</span>
                        </div>
                        {showGross && (
                            <div className="cost-tracking-cell cost-tracking-cell--w-125 text-end py-3">
                                <span className="fw-bold text-secondary">{formatCurrencyToLocale(category.totals.contractSumGross || 0)}</span>
                            </div>
                        )}
                        <div className="cost-tracking-cell cost-tracking-cell--w-130 text-end py-3">
                            <span className="fw-bold text-warning">{formatCurrencyToLocale(category.totals.supplementOpenAmount || 0)}</span>
                        </div>
                        <div className="cost-tracking-cell cost-tracking-cell--w-150 text-end py-3">
                            <span className="fw-bold text-warning">{formatCurrencyToLocale(category.totals.risk || 0)}</span>
                        </div>
                        <div className="cost-tracking-cell cost-tracking-cell--w-140 text-end py-3">
                            <span className="fw-bold text-primary">
                                {formatCurrencyToLocale((category.totals.contractSumNet || 0) + (category.totals.risk || 0) + (category.totals.supplementOpenAmount || 0))}
                            </span>
                        </div>
                        <div className="cost-tracking-cell cost-tracking-cell--w-130 text-end py-3">
                            <span className="fw-bold text-secondary">{formatCurrencyToLocale(category.totals.invoiceAmount || 0)}</span>
                        </div>
                        <div className="cost-tracking-cell cost-tracking-cell--w-125 text-end py-3">
                            <span className="fw-bold text-success">{formatCurrencyToLocale(category.totals.measuredAmountNet || 0)}</span>
                        </div>
                        {showGross && (
                            <div className="cost-tracking-cell cost-tracking-cell--w-125 text-end py-3">
                                <span className="fw-bold text-success">{formatCurrencyToLocale(category.totals.measuredAmountGross || 0)}</span>
                            </div>
                        )}
                        {(() => {
                            const diff = category.totals.actualToAwardDifference || 0;
                            const base = category.totals.contractSumNet || 0;
                            const pct = base !== 0 ? ((diff / base) * 100).toFixed(2) : '0.00';
                            return (
                                <>
                                    <div className="cost-tracking-cell cost-tracking-cell--w-120 text-end py-3">
                                        <span className={`fw-bold ${diff >= 0 ? 'text-success' : 'text-danger'}`}>{formatCurrencyToLocale(diff)}</span>
                                    </div>
                                    <div className="cost-tracking-cell cost-tracking-cell--w-80 text-center py-3">
                                        <span className={`fw-bold ${diff >= 0 ? 'text-success' : 'text-danger'}`}>{formatValueToLocale(pct)}%</span>
                                    </div>
                                </>
                            );
                        })()}
                    </div>
                </div>

                {/* Category Items */}
                {isExpanded && category.items.map(item => <React.Fragment key={item.id}>{renderGroupRow(item, 1)}</React.Fragment>)}
            </>
        );
    };

    const renderGroupRow = (group, level = 0) => {
        const hasChildren = group.children && group.children.length > 0;
        const isExpanded = expandedGroups.has(group.id);
        const isUnassigned = group.code === "N/A" || group.id === 'unassigned';
        const isManual = group.isManual === true;
        const isEmpty = !isManual && !group.companyId && group.lvAmount === 0 && group.supplementAmount === 0 && (group.invoiceAmount || 0) === 0;

        // Level-2 DIN sub-group placeholder rows (no company, all-zero amounts, level set
        // by the backend) must always be shown so the full DIN 276 structure is visible
        // even for categories that have no assigned positions yet.
        const isDinStructurePlaceholder = !group.companyId && group.level > 1;

        // Skip empty groups without children. When showOnlyWithData is true, also skip
        // DIN structure placeholder rows that have no data.
        if (isEmpty && !hasChildren && (!isDinStructurePlaceholder || showOnlyWithData)) {
            return null;
        }

        return (
            <>
                <div
                    key={group.id}
                    className={`cost-tracking-row cost-group-row level-${level} ${isUnassigned ? 'table-warning' : ''} ${isExpanded ? 'expanded' : ''} ${hasChildren ? 'cost-tracking-row--pointer' : ''}`}
                    onClick={() => hasChildren && toggleGroup(group.id)}
                >
                    {/* Fixed Columns */}
                    <div className="cost-tracking-row-fixed">
                        {/* DIN 276 KGR Column */}
                        <div className={`cost-tracking-cell cost-tracking-cell--w-180 cost-tracking-cell--indent-${Math.min(level, 2)}`}>
                            <div className="d-flex align-items-center gap-1">
                                {hasChildren && (
                                    <FontAwesomeIcon
                                        icon={isExpanded ? faChevronDown : faChevronRight}
                                        className="me-1 text-primary cost-tracking-chevron-icon flex-shrink-0"
                                    />
                                )}
                                {!hasChildren && <span className="me-2 cost-tracking-cell-spacer"></span>}
                                {isManual && (
                                    <div className="d-flex gap-1 flex-shrink-0 manual-entry-actions">
                                        {editingManualEntry === group.entryId ? (
                                            <>
                                                <button className="btn-icon btn-icon--save" title={t('common.save')} onClick={() => handleSaveManualEntryEdit(group.entryId, group)}>
                                                    <FontAwesomeIcon icon={faCheck} />
                                                </button>
                                                <button className="btn-icon btn-icon--cancel" title={t('common.cancel')} onClick={handleCancelEdit}>
                                                    <FontAwesomeIcon icon={faCancel} />
                                                </button>
                                            </>
                                        ) : (
                                            <>
                                                {canEdit && (
                                                    <button className="btn-icon btn-icon--edit" title={t('common.edit')} onClick={() => handleEditManualEntry(group.entryId)}>
                                                        <FontAwesomeIcon icon={faEdit} />
                                                    </button>
                                                )}
                                                {canEdit && (
                                                    <button className="btn-icon btn-icon--delete" title={t('common.delete')} onClick={() => handleDeleteManualEntry(group.entryId)}>
                                                        <FontAwesomeIcon icon={faTrash} />
                                                    </button>
                                                )}
                                            </>
                                        )}
                                    </div>
                                )}
                                {isUnassigned ? (
                                    <div className="d-flex align-items-center">
                                        <FontAwesomeIcon icon={faExclamationTriangle} className="me-1 text-warning" />
                                        <strong className="text-warning">Unassigned</strong>
                                    </div>
                                ) : (
                                    <strong className={level === 0 ? 'fs-6' : level === 1 ? 'fs-7' : ''}>∑ KGR {group.code}</strong>
                                )}
                            </div>
                        </div>

                        {/* FIRMA Column */}
                        <div className="cost-tracking-cell cost-tracking-cell--w-150">
                            {isManual ? (
                                editingManualEntry === group.entryId ? (
                                    <TextFieldAtom
                                        size="sm"
                                        value={manualEntryValues[group.entryId]?.companyName !== undefined ? manualEntryValues[group.entryId].companyName : (group.companyName || '')}
                                        onChange={(e) => handleManualEntryFieldChange(group.entryId, 'companyName', e.target.value)}
                                        placeholder={t('costTracking.manual-entry.company-name-placeholder')}
                                        className="cgh-company-name"
                                    />
                                ) : (
                                    <span className="cgh-company-name">
                                        {group.companyName || <span className="text-muted fst-italic cgh-company-name--muted">-</span>}
                                    </span>
                                )
                            ) : (
                                <>
                                    {group.companyName && !isUnassigned && (
                                        <span className="cgh-company-name">
                                            {group.companyName}
                                        </span>
                                    )}
                                    {hasChildren && group.companyId && (
                                        <span className="text-muted small">
                                            ({group.children.length} {t('costTracking.sub-groups')})
                                        </span>
                                    )}
                                </>
                            )}
                        </div>

                        {/* GEWERK Column */}
                        <div className="cost-tracking-cell cost-tracking-cell--w-200">
                            <span className={level === 0 ? 'fw-bold' : level === 1 ? 'fw-semibold' : ''}>{group.name}</span>
                        </div>

                        {/* VERGABE Column - Award Amount */}
                        <div className="cost-tracking-cell text-end cost-tracking-cell--w-130 cost-tracking-cell--input">
                            {isManual && editingManualEntry === group.entryId ? (
                                <TextFieldAtom
                                    type="text"
                                    size="sm"
                                    value={manualEntryValues[group.entryId]?.awardAmount !== undefined ? manualEntryValues[group.entryId].awardAmount : (group.contractSumNet - group.supplementAmount)}
                                    onChange={(e) => handleManualEntryFieldChange(group.entryId, 'awardAmount', e.target.value)}
                                    placeholder="0,00"
                                    decimalPlaces={2}
                                    suffix="€"
                                />
                            ) : (
                                <span className={level === 0 ? 'fw-bold' : level === 1 ? 'fw-semibold' : ''}>
                                    {formatCurrencyToLocale(group.lvAmount || 0)}
                                </span>
                            )}
                        </div>

                        {/* SUPPLEMENTS APPROVED Column */}
                        <div className="cost-tracking-cell text-end text-info cost-tracking-cell--w-130 cost-tracking-cell--input">
                            {isManual && editingManualEntry === group.entryId ? (
                                <TextFieldAtom
                                    type="text"
                                    size="sm"
                                    value={manualEntryValues[group.entryId]?.supplementAmount !== undefined ? manualEntryValues[group.entryId].supplementAmount : group.supplementAmount}
                                    onChange={(e) => handleManualEntryFieldChange(group.entryId, 'supplementAmount', e.target.value)}
                                    placeholder="0,00"
                                    decimalPlaces={2}
                                    suffix="€"
                                />
                            ) : (
                                <span className={level === 0 ? 'fw-bold' : level === 1 ? 'fw-semibold' : ''}>
                                    {formatCurrencyToLocale(group.supplementAmount || 0)}
                                </span>
                            )}
                        </div>

                    </div>

                    {/* Scrollable Columns */}
                    <div className={`cost-tracking-row-scrollable${!showGross ? ' cost-tracking-row-scrollable--compact' : ''}`}>
                        {/* AUFTRAGS-SUMME (Netto) Column - Calculated */}
                        <div className="cost-tracking-cell text-end text-secondary cost-tracking-cell--w-125">
                            <span className={level === 0 ? 'fw-bold' : level === 1 ? 'fw-semibold' : ''}>
                                {formatCurrencyToLocale(group.contractSumNet || 0)}
                            </span>
                        </div>

                        {/* AUFTRAGS-SUMME (Brutto) Column - Calculated */}
                        {showGross && (
                            <div className="cost-tracking-cell text-end text-secondary cost-tracking-cell--w-125">
                                <span className={level === 0 ? 'fw-bold' : level === 1 ? 'fw-semibold' : ''}>
                                    {formatCurrencyToLocale(group.contractSumGross || 0)}
                                </span>
                            </div>
                        )}

                        {/* SUPPLEMENTS OPEN Column */}
                        <div className="cost-tracking-cell text-end text-warning cost-tracking-cell--w-130">
                            <span className={level === 0 ? 'fw-bold' : level === 1 ? 'fw-semibold' : ''}>
                                {formatCurrencyToLocale(group.supplementOpenAmount || 0)}
                            </span>
                        </div>

                        {/* Risk Field Column */}
                        <div className="cost-tracking-cell text-center cost-tracking-cell--w-150 cost-tracking-cell--input">
                            {isManual && editingManualEntry === group.entryId ? (
                                <TextFieldAtom
                                    type="text"
                                    size="sm"
                                    value={manualEntryValues[group.entryId]?.riskAmount !== undefined ? manualEntryValues[group.entryId].riskAmount : group.risk}
                                    onChange={(e) => handleManualEntryFieldChange(group.entryId, 'riskAmount', e.target.value)}
                                    placeholder="0,00"
                                    decimalPlaces={2}
                                    suffix="€"
                                    disabled={!costTrackingId}
                                />
                            ) : level > 0 && !hasChildren ? (
                                <div className="d-flex align-items-center justify-content-center">
                                    <TextFieldAtom
                                        type="number"
                                        size="sm"
                                        value={riskValues[group.id] || ''}
                                        onChange={(e) => handleRiskChange(group.id, e.target.value)}
                                        onBlur={() => handleRiskBlur(group.id)}
                                        placeholder="0.00"
                                        step="0.01"
                                        suffix="€"
                                        disabled={!costTrackingId || !canEdit}
                                    />
                                </div>
                            ) : (
                                <span className={level === 0 ? 'fw-bold' : level === 1 ? 'fw-semibold' : ''}>
                                    -
                                </span>
                            )}
                        </div>

                        {/* Forecast Completion Including Risk Column */}
                        <div className="cost-tracking-cell text-end text-primary cost-tracking-cell--w-140">
                            <span className={level === 0 ? 'fw-bold' : level === 1 ? 'fw-semibold' : ''}>
                                {formatCurrencyToLocale((group.contractSumNet || 0) + (parseFloat(riskValues[group.id]) || group.risk || 0) + (group.supplementOpenAmount || 0))}
                            </span>
                        </div>

                        {/* Invoice Amount - Actual Invoiced */}
                        <div className="cost-tracking-cell text-end text-secondary cost-tracking-cell--w-130 cost-tracking-cell--input">
                            {isManual && editingManualEntry === group.entryId ? (
                                <TextFieldAtom
                                    type="text"
                                    size="sm"
                                    value={manualEntryValues[group.entryId]?.actualInvoicedAmount !== undefined ? manualEntryValues[group.entryId].actualInvoicedAmount : group.invoiceAmount}
                                    onChange={(e) => handleManualEntryFieldChange(group.entryId, 'actualInvoicedAmount', e.target.value)}
                                    placeholder="0,00"
                                    decimalPlaces={2}
                                    suffix="€"
                                />
                            ) : (
                                <span className={level === 0 ? 'fw-bold' : level === 1 ? 'fw-semibold' : ''}>
                                    {formatCurrencyToLocale(group.invoiceAmount || 0)}
                                </span>
                            )}
                        </div>

                        {/* ISTKOSTEN (Netto) Column - Measured Net */}
                        <div className="cost-tracking-cell text-end text-success cost-tracking-cell--w-125 cost-tracking-cell--input">
                            {isManual && editingManualEntry === group.entryId ? (
                                <TextFieldAtom
                                    type="text"
                                    size="sm"
                                    value={manualEntryValues[group.entryId]?.measuredNetAmount !== undefined ? manualEntryValues[group.entryId].measuredNetAmount : group.measuredAmountNet}
                                    onChange={(e) => handleManualEntryFieldChange(group.entryId, 'measuredNetAmount', e.target.value)}
                                    placeholder="0,00"
                                    decimalPlaces={2}
                                    suffix="€"
                                />
                            ) : (
                                <span className={level === 0 ? 'fw-bold' : level === 1 ? 'fw-semibold' : ''}>
                                    {formatCurrencyToLocale(group.measuredAmountNet || 0)}
                                </span>
                            )}
                        </div>

                        {/* ISTKOSTEN (Brutto) Column */}
                        {showGross && (
                            <div className="cost-tracking-cell text-end text-success cost-tracking-cell--w-125">
                                <span className={level === 0 ? 'fw-bold' : level === 1 ? 'fw-semibold' : ''}>
                                    {formatCurrencyToLocale(group.measuredAmountGross || 0)}
                                </span>
                            </div>
                        )}

                        {/* Differenz IST zu Vergabebudget - Amount + % Columns */}
                        {(() => {
                            const diff = group.actualToAwardDifference || 0;
                            const base = group.contractSumNet || 0;
                            const pct = base !== 0 ? ((diff / base) * 100).toFixed(2) : '0.00';
                            return (
                                <>
                                    <div className="cost-tracking-cell text-end cost-tracking-cell--w-120">
                                        <span className={`${level === 0 ? 'fw-bold' : level === 1 ? 'fw-semibold' : ''} ${diff >= 0 ? 'text-success' : 'text-danger'}`}>
                                            {formatCurrencyToLocale(diff)}
                                        </span>
                                    </div>
                                    <div className="cost-tracking-cell text-center cost-tracking-cell--w-80">
                                        <span className={`${level === 0 ? 'fw-bold' : level === 1 ? 'fw-semibold' : ''} ${diff >= 0 ? 'text-success' : 'text-danger'}`}>
                                            {formatValueToLocale(pct)}%
                                        </span>
                                    </div>
                                </>
                            );
                        })()}
                    </div>
                </div>

                {/* Render children when expanded */}
                {hasChildren && isExpanded && group.children.map(child => <React.Fragment key={child.id}>{renderGroupRow(child, level + 1)}</React.Fragment>)}
            </>
        );
    };

    const hierarchyData = hierarchy?.grouped || [];
    const displayedHierarchy = showOnlyWithData
        ? hierarchyData.filter(categoryHasData)
        : hierarchyData;

    if (!hierarchy || !hierarchy.grouped || hierarchy.grouped.length === 0) {
        return (
            <Card className="shadow-sm border-0">
                <Card.Header className="bg-gradient-primary text-white border-0 py-3">
                    <h5 className="mb-0 fw-bold">{t("costTracking.cost-tracking-list")}</h5>
                </Card.Header>
                <Card.Body className="text-center text-muted py-5">
                    <FontAwesomeIcon icon={faExclamationTriangle} className="mb-2" size="2x" />
                    <div>{t('costTracking.no-data')}</div>
                </Card.Body>
            </Card>
        );
    }

    return (
        <Card className="shadow-sm cost-hierarchy-card">
            <Card.Header className="card-header">
                <div className="d-flex justify-content-between align-items-center">
                    <h5 className="header-title">{t("costTracking.cost-tracking-list")}</h5>
                    <div className="action-buttons">
                        {canEdit && (
                            <Button
                                size="sm"
                                onClick={handleAddManualEntry}
                                className="btn-add-manual"
                                variant="success"
                                disabled={!costTrackingId}
                                title={!costTrackingId ? t("costTracking.no-active-version") : undefined}
                            >
                                <FontAwesomeIcon icon={faPlus} />
                                {t("costTracking.add-manual")}
                            </Button>
                        )}
                        <Button
                            size="sm"
                            onClick={expandAll}
                            className="btn-expand"
                        >
                            <FontAwesomeIcon icon={faExpandAlt} />
                            {t("costTracking.expand-all")}
                        </Button>
                        <Button
                            size="sm"
                            onClick={collapseAll}
                            className="btn-collapse"
                        >
                            <FontAwesomeIcon icon={faCompressAlt} />
                            {t("costTracking.collapse-all")}
                        </Button>
                    </div>
                </div>
            </Card.Header>
            <Card.Body className="card-body">
                <div className="table-container">
                    {/* Horizontal Scroll Container */}
                    <div className="cost-tracking-scroll-container">
                        {/* Fixed Header Section */}
                        <div className="cost-tracking-header">
                            <div className="cost-tracking-header-fixed">
                                <div className="cost-tracking-header-row">
                                    <div className="cost-tracking-header-cell cost-tracking-header-cell--w-180" rowSpan={2}>
                                        {t('costTracking.din-276-kgr')}
                                    </div>
                                    <div className="cost-tracking-header-cell cost-tracking-header-cell--w-150" rowSpan={2}>
                                        {t('costTracking.firma')}
                                    </div>
                                    <div className="cost-tracking-header-cell cost-tracking-header-cell--w-200" rowSpan={2}>
                                        {t('costTracking.gewerk')}
                                    </div>
                                    <div className="cost-tracking-header-cell text-end cost-tracking-header-cell--w-130" rowSpan={2}>
                                        {t('costTracking.vergabe')}
                                    </div>
                                    <div className="cost-tracking-header-cell text-end cost-tracking-header-cell--w-130" rowSpan={2}>
                                        {t('costTracking.supplements-approved')}
                                    </div>
                                </div>
                            </div>
                            {/* Scrollable Header Section */}
                            <div className={`cost-tracking-header-scrollable${!showGross ? ' cost-tracking-header-scrollable--compact' : ''}`}>
                                <div className="cost-tracking-header-row">
                                    <div className={`cost-tracking-header-cell text-center border-end border-secondary ${showGross ? 'cost-tracking-header-cell--w-250' : 'cost-tracking-header-cell--w-125'}`}>
                                        {t('costTracking.auftragssumme')}
                                    </div>
                                    <div className="cost-tracking-header-cell text-end border-end border-secondary cost-tracking-header-cell--w-130" rowSpan={2}>
                                        {t('costTracking.supplements-open')}
                                    </div>
                                    <div className="cost-tracking-header-cell text-center border-end border-secondary cost-tracking-header-cell--w-150">
                                        {t('costTracking.risk')}
                                    </div>
                                    <div className="cost-tracking-header-cell text-end border-end border-secondary cost-tracking-header-cell--w-140">
                                        {t('costTracking.forecast-completion-with-risk')}
                                    </div>
                                    <div className="cost-tracking-header-cell text-end border-end border-secondary cost-tracking-header-cell--w-130">
                                        {t('costTracking.actual-invoiced')}
                                    </div>
                                    <div className={`cost-tracking-header-cell text-center border-end border-secondary ${showGross ? 'cost-tracking-header-cell--w-250' : 'cost-tracking-header-cell--w-125'}`}>
                                        {t('costTracking.measured')}
                                    </div>
                                    <div className="cost-tracking-header-cell text-center border-start border-secondary cost-tracking-header-cell--w-200">
                                        {t('costTracking.differenz-ist-vergabe')}
                                    </div>
                                </div>
                                {/* Sub Header Row */}
                                <div className="cost-tracking-header-row cost-tracking-sub-header border-top border-secondary">
                                    {!showGross && (
                                        <div className="cost-tracking-header-cell text-end cost-tracking-header-cell--w-125">
                                            {t('costTracking.netto')}
                                        </div>
                                    )}
                                    {showGross && (
                                        <>
                                            <div className="cost-tracking-header-cell text-end cost-tracking-header-cell--w-125">
                                                {t('costTracking.netto')}
                                            </div>
                                            <div className="cost-tracking-header-cell text-end cost-tracking-header-cell--w-125">
                                                {t('costTracking.brutto')}
                                            </div>
                                        </>
                                    )}
                                    <div className="cost-tracking-header-cell text-end cost-tracking-header-cell--w-130">
                                        {/* Supplements Open - no sub-label */}
                                    </div>
                                    <div className="cost-tracking-header-cell text-end cost-tracking-header-cell--w-150">
                                        {/* Risk - single column */}
                                    </div>
                                    <div className="cost-tracking-header-cell text-end cost-tracking-header-cell--w-140">
                                        {/* Forecast - single column */}
                                    </div>
                                    <div className="cost-tracking-header-cell text-end cost-tracking-header-cell--w-130">
                                        {/* Invoice - single column */}
                                    </div>
                                    {!showGross && (
                                        <div className="cost-tracking-header-cell text-end cost-tracking-header-cell--w-125">
                                            {t('costTracking.netto')}
                                        </div>
                                    )}
                                    {showGross && (
                                        <>
                                            <div className="cost-tracking-header-cell text-end cost-tracking-header-cell--w-125">
                                                {t('costTracking.netto')}
                                            </div>
                                            <div className="cost-tracking-header-cell text-end cost-tracking-header-cell--w-125">
                                                {t('costTracking.brutto')}
                                            </div>
                                        </>
                                    )}
                                    <div className="cost-tracking-header-cell text-end border-start border-secondary cost-tracking-header-cell--w-120">
                                        {t('costTracking.amount')}
                                    </div>
                                    <div className="cost-tracking-header-cell text-center cost-tracking-header-cell--w-80">
                                        %
                                    </div>
                                </div>
                            </div>
                        </div>
                        {/* Table Body */}
                        <div className="cost-tracking-body">
                            {displayedHierarchy.map(category => <React.Fragment key={category.mainCategory}>{renderCategoryGroup(category)}</React.Fragment>)}
                        </div>
                        {/* Summary Footer Row - sticky to bottom of scroll container */}
                        {hierarchyData.length > 0 && (() => {
                            const totals = hierarchyData.reduce((acc, cat) => {
                                acc.lvAmount += cat.totals.lvAmount || 0;
                                acc.supplementAmount += cat.totals.supplementAmount || 0;
                                acc.contractSumNet += cat.totals.contractSumNet || 0;
                                acc.contractSumGross += cat.totals.contractSumGross || 0;
                                acc.supplementOpenAmount += cat.totals.supplementOpenAmount || 0;
                                acc.risk += cat.totals.risk || 0;
                                acc.invoiceAmount += cat.totals.invoiceAmount || 0;
                                acc.measuredAmountNet += cat.totals.measuredAmountNet || 0;
                                acc.measuredAmountGross += cat.totals.measuredAmountGross || 0;
                                acc.actualToAwardDifference += cat.totals.actualToAwardDifference || 0;
                                return acc;
                            }, {
                                lvAmount: 0, supplementAmount: 0, contractSumNet: 0, contractSumGross: 0,
                                supplementOpenAmount: 0, risk: 0, invoiceAmount: 0,
                                measuredAmountNet: 0, measuredAmountGross: 0, actualToAwardDifference: 0,
                            });
                            const forecastTotal = totals.contractSumNet + totals.risk + totals.supplementOpenAmount;
                            const diffPct = totals.contractSumNet !== 0
                                ? ((totals.actualToAwardDifference / totals.contractSumNet) * 100).toFixed(1)
                                : '0.0';

                            return (
                                <div className="cost-tracking-footer">
                                    {/* Fixed Columns */}
                                    <div className="cost-tracking-row-fixed">
                                        <div className="cost-tracking-cell py-3 cost-tracking-cell--w-180">
                                            {t('costTracking.total-summary')}
                                        </div>
                                        <div className="cost-tracking-cell py-3 cost-tracking-cell--w-150" />
                                        <div className="cost-tracking-cell py-3 cost-tracking-cell--w-200" />
                                        <div className="cost-tracking-cell text-end py-3 cost-tracking-cell--w-130">
                                            {formatCurrencyToLocale(totals.lvAmount)}
                                        </div>
                                        <div className="cost-tracking-cell text-end py-3 cost-tracking-cell--w-130 cost-tracking-cell--supplement">
                                            {formatCurrencyToLocale(totals.supplementAmount)}
                                        </div>
                                    </div>
                                    {/* Scrollable Columns */}
                                    <div className={`cost-tracking-row-scrollable${!showGross ? ' cost-tracking-row-scrollable--compact' : ''}`}>
                                        <div className="cost-tracking-cell text-end py-3 cost-tracking-cell--w-125 cost-tracking-cell--muted">
                                            {formatCurrencyToLocale(totals.contractSumNet)}
                                        </div>
                                        {showGross && (
                                            <div className="cost-tracking-cell text-end py-3 cost-tracking-cell--w-125 cost-tracking-cell--muted">
                                                {formatCurrencyToLocale(totals.contractSumGross)}
                                            </div>
                                        )}
                                        <div className="cost-tracking-cell text-end py-3 cost-tracking-cell--w-130 cost-tracking-cell--warn">
                                            {formatCurrencyToLocale(totals.supplementOpenAmount)}
                                        </div>
                                        <div className="cost-tracking-cell text-end py-3 cost-tracking-cell--w-150 cost-tracking-cell--warn">
                                            {formatCurrencyToLocale(totals.risk)}
                                        </div>
                                        <div className="cost-tracking-cell text-end py-3 cost-tracking-cell--w-140 cost-tracking-cell--forecast">
                                            {formatCurrencyToLocale(forecastTotal)}
                                        </div>
                                        <div className="cost-tracking-cell text-end py-3 cost-tracking-cell--w-130 cost-tracking-cell--muted">
                                            {formatCurrencyToLocale(totals.invoiceAmount)}
                                        </div>
                                        <div className="cost-tracking-cell text-end py-3 cost-tracking-cell--w-125 cost-tracking-cell--success">
                                            {formatCurrencyToLocale(totals.measuredAmountNet)}
                                        </div>
                                        {showGross && (
                                            <div className="cost-tracking-cell text-end py-3 cost-tracking-cell--w-125 cost-tracking-cell--success">
                                                {formatCurrencyToLocale(totals.measuredAmountGross)}
                                            </div>
                                        )}
                                        <div className={`cost-tracking-cell text-end py-3 cost-tracking-cell--w-120 ${totals.actualToAwardDifference >= 0 ? 'cost-tracking-cell--success' : 'cost-tracking-cell--danger'}`}>
                                            {formatCurrencyToLocale(totals.actualToAwardDifference)}
                                        </div>
                                        <div className={`cost-tracking-cell text-center py-3 cost-tracking-cell--w-80 ${totals.actualToAwardDifference >= 0 ? 'cost-tracking-cell--success' : 'cost-tracking-cell--danger'}`}>
                                            {diffPct}%
                                        </div>
                                    </div>
                                </div>
                            );
                        })()}
                    </div>
                </div>
            </Card.Body>

            <AddManualEntryModal
                show={showManualEntryModal}
                handleClose={handleCloseManualEntryModal}
                handleSave={handleSaveManualEntry}
                costGroups={costGroups}
            />
        </Card>
    );
}

export default CostGroupHierarchy;